import { cx } from '@vizit/theme';
import { ProgressBar, type ProgressBarProps, type ProgressBarRenderProps } from 'react-aria-components';
import { composeTailwindRenderProps } from '../../utils';

interface _ProgressBarRenderProps extends ProgressBarRenderProps {
  minValue: number;
  maxValue: number;
}

interface _ProgressBarProps extends Omit<ProgressBarProps, 'children'> {
  /**
   * Classes to apply to the bar element
   *
   * @optional
   * @type string
   */
  barClassName?: string;
  /**
   * The children of the component. A function may be provided to alter the children based on component state.
   */
  children?: (renderProps: _ProgressBarRenderProps) => React.ReactNode | React.ReactNode;
  /**
   * Classes to apply to the fill element
   *
   * @optional
   * @type string
   */
  fillClassName?: string;
  /**
   * Style the progress bar. Overrides the fillClassName if both are provided.
   *
   * @optional
   * @default 'progress'
   * @type 'success' | 'progress'
   */
  status?: 'success' | 'progress';
}

function _ProgressBar(props: _ProgressBarProps) {
  const {
    barClassName,
    className,
    children,
    minValue = 0,
    maxValue = 100,
    fillClassName,
    status = 'progress',
    formatOptions = { maximumFractionDigits: 2, minimumFractionDigits: 0 },
    value,
    ...other
  } = props;

  const clampPercentage = (percentage: number) => Math.min(100, Math.max(0, percentage));

  return (
    <ProgressBar
      className={composeTailwindRenderProps(className, 'vzt-linear-progress w-full')}
      formatOptions={formatOptions}
      maxValue={maxValue}
      minValue={minValue}
      value={value}
      {...other}
    >
      {(values) => (
        <>
          {typeof children === 'function'
            ? children({
                valueText: values.valueText,
                percentage: values.percentage,
                minValue,
                maxValue,
                isIndeterminate: values.isIndeterminate,
              })
            : children}
          <div
            className={cx(
              'vzt-linear-progress__bar',
              'relative h-2 w-full overflow-hidden rounded-full bg-gray-light-300',
              barClassName,
            )}
          >
            <div
              className={cx(
                'vzt-linear-progress__fill',
                'h-2 flex-1 rounded-full transition-[width] duration-300 ease-out',
                {
                  'bg-brand-600': status === 'progress',
                  'bg-success-500': status === 'success',
                },
                fillClassName,
              )}
              style={{ width: `${clampPercentage(values.percentage || 0)}%` }}
            />
          </div>
        </>
      )}
    </ProgressBar>
  );
}

export type { _ProgressBarProps as ProgressBarProps, _ProgressBarRenderProps as ProgressBarRenderProps };
export { _ProgressBar as ProgressBar };
